import * as THREE from 'three';
import gsap from 'gsap';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import DragRotate from './DragRotate.js';

/*
 * Modèles 3D des onglets de l'accordéon.
 *
 * Un seul canvas (et donc un seul contexte WebGL) couvre tout l'accordéon, sans
 * capter la souris. À chaque image, chaque modèle est placé sur le petit
 * élément .panel-model de son onglet (qui suit l'animation CSS de l'onglet) et
 * dessiné uniquement dans le rectangle de cet onglet (scissor), ce qui donne
 * le même effet de découpe que l'ancienne image.
 *
 * Chaque onglet choisit son modèle avec data-model sur le <a class="panel">.
 * Seul l'onglet ouvert se manipule à la souris (clic maintenu + glisser).
 */

const FOV = 30; // champ de vision de la caméra (degrés)
const FIT = 0.8; // part de la zone .panel-model occupée par le modèle

export default class AccordionModels {
  constructor({ element, canvas, panels }) {
    this.element = element;
    this.canvas = canvas;
    this.cursors = [];

    this.items = panels
      .map((panel) => ({
        panel,
        slot: panel.querySelector('.panel-model'),
        url: panel.dataset.model,
      }))
      .filter((item) => item.slot && item.url);

    if (!this.canvas || this.items.length === 0) {
      console.warn(
        'AccordionModels : il faut un canvas et des onglets avec un .panel-model et un data-model.',
      );
      return;
    }

    this.width = 0;
    this.height = 0;
    this.visible = false;
    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    this.items.forEach((item, index) => {
      item.pivot = null;
      item.spinner = null;
      item.tilt = null;
      item.speed = 0;
      item.drag = new DragRotate({
        hitTest: (x, y) => this.isOver(item, x, y),
        // Les onglets sont des liens : on les laisse démarrer un glissement
        interactive:
          'button, input, textarea, select, label, summary, video, iframe, [role="button"]',
        onCursor: (value) => this.setCursor(index, value),
      });
    });

    this.tick = this.tick.bind(this);
    this.init();
  }

  // Un glissement est en cours (pour ne pas changer d'onglet pendant ce temps)
  get isDragging() {
    return this.items.some((item) => item.drag.dragging);
  }

  // Un glissement vient de se terminer (pour ne pas suivre le lien au relâchement)
  get justDragged() {
    return this.items.some((item) => item.drag.justDragged);
  }

  init() {
    this.createRenderer();
    this.createScene();
    this.loadModels();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.canvas);
    this.resize();

    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
    });
    this.observer.observe(this.element);

    // Même ticker que GSAP (ScrollSmoother) pour rester synchro avec le défilement
    gsap.ticker.add(this.tick);
  }

  /* ---------- Three.js ---------- */

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.renderer.autoClear = false; // on efface nous-mêmes, une fois par image
  }

  createScene() {
    this.scene = new THREE.Scene();

    // 1 unité = 1 px à z = 0 (voir resize), pour placer les modèles au pixel près
    this.camera = new THREE.PerspectiveCamera(FOV, 1, 10, 20000);

    // Tes modèles sont en métal (PBR) : sans environnement à refléter, un métal
    // s'affiche presque noir, donc invisible sur le fond sombre des onglets.
    // RoomEnvironment fournit un décor de studio à refléter.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(
      new RoomEnvironment(),
      0.04,
    ).texture;
    pmrem.dispose();

    const ambient = new THREE.AmbientLight('#ffffff', 0.4);
    const key = new THREE.DirectionalLight('#ffffff', 1.8);
    key.position.set(-1, 2, 3);
    const fill = new THREE.DirectionalLight('#ffffff', 0.6);
    fill.position.set(2, 0.5, 1);
    this.scene.add(ambient, key, fill);
  }

  // Chaque fichier n'est chargé qu'une fois, même si plusieurs onglets l'utilisent
  loadModels() {
    const loader = new GLTFLoader();
    const urls = [...new Set(this.items.map((item) => item.url))];

    urls.forEach((url) => {
      loader.load(
        url,
        (gltf) => this.createModels(url, gltf.scene),
        undefined,
        (error) => {
          console.warn(
            `AccordionModels : impossible de charger ${url}. Un modèle de remplacement est affiché.`,
            error,
          );
          this.createModels(url, this.createFallback());
        },
      );
    });
  }

  createFallback() {
    return new THREE.Mesh(
      new THREE.TorusKnotGeometry(0.3, 0.1, 160, 24),
      new THREE.MeshStandardMaterial({
        color: '#6c717a',
        roughness: 0.4,
        metalness: 0.1,
      }),
    );
  }

  // Un clone du modèle pour chaque onglet qui utilise cette url
  createModels(url, source) {
    source.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(source);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;

    this.items.forEach((item, index) => {
      if (item.url !== url) return;

      const model = source.clone();
      model.position.copy(center).multiplyScalar(-1); // centre le modèle sur son milieu

      const normalized = new THREE.Group(); // ramène le modèle à une taille de 1
      normalized.scale.setScalar(1 / maxDim);
      normalized.add(model);

      const spinner = new THREE.Group(); // rotation continue + glissement
      spinner.rotation.y = index * 1.2;
      spinner.add(normalized);

      const tilt = new THREE.Group(); // inclinaison à la souris
      tilt.add(spinner);

      const pivot = new THREE.Group(); // position et taille en px
      pivot.visible = false;
      pivot.add(tilt);
      this.scene.add(pivot);

      item.pivot = pivot;
      item.spinner = spinner;
      item.tilt = tilt;
    });
  }

  resize() {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (!width || !height) return;

    this.width = width;
    this.height = height;

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);

    this.camera.aspect = width / height;
    this.camera.position.z =
      height / 2 / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    this.camera.updateProjectionMatrix();
  }

  tick(time, deltaTime) {
    if (!this.visible || !this.width) return;

    const dt = Math.min(deltaTime, 100) / 1000;
    const canvasRect = this.canvas.getBoundingClientRect();

    // Efface toute l'image précédente (les onglets bougent pendant l'animation)
    this.renderer.setScissorTest(false);
    this.renderer.clear();
    this.renderer.setScissorTest(true);

    for (const item of this.items) {
      if (!item.pivot) continue;

      const panelRect = item.panel.getBoundingClientRect();
      const slotRect = item.slot.getBoundingClientRect();
      if (!panelRect.width || !slotRect.width) continue;

      // Rotation continue (en pause pendant le glissement) + glissement à la souris
      const isActive = item.panel.classList.contains('active');
      const targetSpeed =
        this.reduceMotion || item.drag.dragging ? 0 : isActive ? 0.5 : 0.25;
      item.speed += (targetSpeed - item.speed) * 0.05;
      item.spinner.rotation.y += item.speed * dt + item.drag.update(dt);
      item.tilt.rotation.x = item.drag.tilt;

      // Place le modèle sur son emplacement (coordonnées du canvas -> scène)
      const x = slotRect.left - canvasRect.left + slotRect.width / 2;
      const y = slotRect.top - canvasRect.top + slotRect.height / 2;
      item.pivot.position.set(x - this.width / 2, this.height / 2 - y, 0);
      item.pivot.scale.setScalar(
        Math.min(slotRect.width, slotRect.height) * FIT,
      );

      // Ne dessine que dans le rectangle de l'onglet (y part du bas du canvas)
      this.renderer.setScissor(
        panelRect.left - canvasRect.left,
        this.height - (panelRect.bottom - canvasRect.top),
        panelRect.width,
        panelRect.height,
      );

      item.pivot.visible = true;
      this.renderer.render(this.scene, this.camera);
      item.pivot.visible = false;
    }
  }

  /* ---------- Souris ---------- */

  // Le pointeur est-il sur le modèle de l'onglet ouvert ? (le canvas ne capte pas la souris)
  isOver(item, x, y) {
    if (!item.pivot || !item.panel.classList.contains('active')) return false;

    const panel = item.panel.getBoundingClientRect();
    const slot = item.slot.getBoundingClientRect();

    return (
      x >= Math.max(panel.left, slot.left) &&
      x <= Math.min(panel.right, slot.right) &&
      y >= Math.max(panel.top, slot.top) &&
      y <= Math.min(panel.bottom, slot.bottom)
    );
  }

  // Le curseur est porté par l'accordéon (les onglets ont leur propre curseur)
  setCursor(index, value) {
    this.cursors[index] = value;
    const cursor = this.cursors.find(Boolean);

    if (cursor) {
      this.element.dataset.cursor = cursor;
    } else {
      delete this.element.dataset.cursor;
    }
  }

  destroy() {
    gsap.ticker.remove(this.tick);
    this.resizeObserver?.disconnect();
    this.observer?.disconnect();
    this.items.forEach((item) => item.drag.destroy());
    this.renderer?.dispose();
  }
}
